import { useId } from 'react';
import { cn } from '@/lib/utils';

interface IconProps {
  className?: string;
  onClick?: () => void;
}

// Hygge Intelligence — 3D owl egg icon with auto-blink animation
export function HyggeAiIcon({ className, onClick }: IconProps) {
  const reactId = useId();
  const id = `hygge-owl-${reactId.replace(/:/g, '')}`;

  return (
    <svg
      className={cn('h-6 w-6', className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      onClick={onClick}
    >
      <defs>
        {/* Egg body gradient — 3D depth */}
        <radialGradient id={`${id}-body`} cx="0.4" cy="0.35" r="0.65">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.45" />
          <stop offset="70%" stopColor="currentColor" stopOpacity="0.2" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.08" />
        </radialGradient>
        {/* Highlight sheen */}
        <radialGradient id={`${id}-sheen`} cx="0.35" cy="0.25" r="0.4">
          <stop offset="0%" stopColor="white" stopOpacity="0.3" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        {/* Eye glow */}
        <radialGradient id={`${id}-eye`} cx="0.45" cy="0.4" r="0.5">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
        </radialGradient>
        {/* Clip path for eyelid blink — ellipse shrinks vertically to simulate closing */}
        {/* Blink cycle: 4s total. Eyes open 93% of the time, quick blink at the end */}
        <clipPath id={`${id}-lclip`}>
          <ellipse cx="9" cy="11.5" rx="3" ry="3">
            <animate
              attributeName="ry"
              values="3;3;0.3;3"
              keyTimes="0;0.9;0.95;1"
              dur="4s"
              begin="2s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0 0 1 1;0.4 0 0.2 1;0.4 0 0.2 1"
            />
          </ellipse>
        </clipPath>
        <clipPath id={`${id}-rclip`}>
          <ellipse cx="15" cy="11.5" rx="3" ry="3">
            <animate
              attributeName="ry"
              values="3;3;0.3;3"
              keyTimes="0;0.9;0.95;1"
              dur="4s"
              begin="2s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0 0 1 1;0.4 0 0.2 1;0.4 0 0.2 1"
            />
          </ellipse>
        </clipPath>
      </defs>

      {/* Egg body — smooth oval with ear tufts */}
      <ellipse cx="12" cy="12.5" rx="8.5" ry="9.5" fill={`url(#${id}-body)`} />
      <ellipse cx="12" cy="12.5" rx="8.5" ry="9.5" stroke="currentColor" strokeWidth="0.8" opacity="0.3" />

      {/* 3D highlight sheen */}
      <ellipse cx="10" cy="9" rx="5" ry="4.5" fill={`url(#${id}-sheen)`} />

      {/* Ear tufts */}
      <path d="M5.5 5.5L7.5 9 4.5 8.5z" fill="currentColor" opacity="0.25" />
      <path d="M18.5 5.5L16.5 9 19.5 8.5z" fill="currentColor" opacity="0.25" />

      {/* Eye sockets — always visible behind clip */}
      <circle cx="9" cy="11.5" r="2.8" fill="currentColor" opacity="0.15" />
      <circle cx="15" cy="11.5" r="2.8" fill="currentColor" opacity="0.15" />

      {/* Left eye — clipped for blink */}
      <g clipPath={`url(#${id}-lclip)`}>
        <circle cx="9" cy="11.5" r="2" fill={`url(#${id}-eye)`} />
        <circle cx="9" cy="11.5" r="1" fill="currentColor" opacity="0.85" />
        <circle cx="8.3" cy="10.9" r="0.55" fill="white" opacity="0.7" />
        <circle cx="9.5" cy="12" r="0.25" fill="white" opacity="0.4" />
      </g>

      {/* Right eye — clipped for blink */}
      <g clipPath={`url(#${id}-rclip)`}>
        <circle cx="15" cy="11.5" r="2" fill={`url(#${id}-eye)`} />
        <circle cx="15" cy="11.5" r="1" fill="currentColor" opacity="0.85" />
        <circle cx="14.3" cy="10.9" r="0.55" fill="white" opacity="0.7" />
        <circle cx="15.5" cy="12" r="0.25" fill="white" opacity="0.4" />
      </g>

      {/* Brow — soft V */}
      <path d="M6 9L12 7.5 18 9" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.35" />

      {/* Beak — small triangle */}
      <path d="M11 14.2L12 16.2 13 14.2" fill="currentColor" opacity="0.4" />
      <path d="M11 14.2L12 16.2 13 14.2" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.3" strokeLinejoin="round" />

      {/* Bottom shadow for 3D ground feel */}
      <ellipse cx="12" cy="21.5" rx="6" ry="0.8" fill="currentColor" opacity="0.06" />
    </svg>
  );
}

// Anthropic Claude — starburst/spark mark
export function AnthropicIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-5 w-5', className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
    >
      <path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z" />
    </svg>
  );
}

// OpenAI — hexagonal flower logo
export function OpenAIIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-5 w-5', className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
    >
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
    </svg>
  );
}

// Google Gemini — 4-point star
export function GeminiIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-5 w-5', className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
    >
      <path d="M12 0C12 6.627 6.627 12 0 12c6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12z" />
    </svg>
  );
}

// Groq — stylised "G" mark
export function GroqIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-5 w-5', className)}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
    >
      <path d="M12.036 2c-3.853-.035-7 3-7.036 6.781-.035 3.782 3.055 6.872 6.908 6.907h2.42v-2.566h-2.292c-2.407.028-4.38-1.866-4.408-4.23-.029-2.362 1.901-4.298 4.308-4.326h.1c2.407 0 4.358 1.915 4.365 4.278v6.305c0 2.342-1.944 4.25-4.323 4.279a4.375 4.375 0 01-3.033-1.252l-1.851 1.818A7 7 0 0012.029 22h.092c3.803-.056 6.858-3.083 6.879-6.816v-6.5C18.907 4.963 15.817 2 12.036 2z" />
    </svg>
  );
}
